import { usePersistedState } from "@/lib/persistedFilters";
import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { AlertTriangle, ArrowLeft, ExternalLink, FileDown, Loader2, Pencil, Search, ShieldAlert, Sparkles, Table, Undo2 } from "lucide-react";
import api, { API } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { KbStyleInjector } from "@/lib/kbSettings";
import { toast } from "sonner";

const DEFAULTS = ["danger", "warning"];

/** Sweep of every callout box in the KB — Danger and Warning by default, with a
 *  triage pass (risk suggestions, unfinished boxes, promote, modernise) and
 *  CSV / PDF exports of whatever is currently filtered. */
export default function KBCalloutAudit() {
  const [data, setData] = useState(null);
  const [variants, setVariants] = useState(DEFAULTS);
  const [q, setQ] = usePersistedState("kb-callouts.q", "");
  const [flag, setFlag] = useState("");
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState("");
  const [undos, setUndos] = useState({});
  const [limit, setLimit] = useState(40);

  const query = useCallback(() => {
    const params = new URLSearchParams();
    params.set("variants", variants.length ? variants.join(",") : DEFAULTS.join(","));
    if (q.trim()) params.set("q", q.trim());
    if (flag) params.set("flag", flag);
    return params.toString();
  }, [variants, q, flag]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data: d } = await api.get(`/kb/callouts/audit?${query()}`);
      setData(d);
    } catch {
      toast.error("Couldn't run the callout sweep");
    } finally { setLoading(false); }
  }, [query]);
  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [load]);

  const download = async (kind) => {
    const res = await fetch(`${API}/kb/callouts/audit.${kind}?${query()}`, { credentials: "include" });
    if (!res.ok) { toast.error(`${kind.toUpperCase()} export failed`); return; }
    const a = document.createElement("a");
    a.href = URL.createObjectURL(await res.blob());
    a.download = `kb-callout-audit.${kind}`;
    a.click();
    URL.revokeObjectURL(a.href);
  };

  const promote = async (row, modernise = false) => {
    setBusy(row.id);
    try {
      const { data: res } = await api.post("/kb/callouts/promote", {
        slug: row.slug, index: row.index, variant: "danger", modernise,
      });
      if (!res.changed) { toast.info("That box is already a Danger box"); return; }
      // An inline undo pill on the row, not a toast that vanishes mid-decision.
      setUndos((u) => ({ ...u, [row.id]: { change_id: res.change_id, before: res.before,
                                           article: row.article, modernised: res.modernised } }));
      toast.success(modernise ? "Rebuilt as a house Danger callout" : "Promoted to Danger");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't change that box");
    } finally { setBusy(""); }
  };

  const undo = async (rowId) => {
    const entry = undos[rowId];
    if (!entry) return;
    setBusy(rowId);
    try {
      await api.post(`/kb/callouts/undo/${entry.change_id}`);
      setUndos((u) => { const n = { ...u }; delete n[rowId]; return n; });
      toast.success("Change undone");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't undo that");
    } finally { setBusy(""); }
  };

  const toggle = (v) => setVariants((cur) =>
    cur.includes(v) ? cur.filter((x) => x !== v) : [...cur, v]);

  // One card per article, its boxes underneath — 600+ standalone cards is a wall.
  const groups = [];
  const bySlug = new Map();
  for (const c of data?.callouts || []) {
    let g = bySlug.get(c.slug);
    if (!g) {
      g = { slug: c.slug, article: c.article, category: c.category, rows: [] };
      bySlug.set(c.slug, g);
      groups.push(g);
    }
    g.rows.push(c);
  }
  useEffect(() => { setLimit(40); }, [variants, q, flag]);

  return (
    <div className="mx-auto max-w-6xl space-y-4 p-6" data-testid="kb-callout-audit-page">
      <KbStyleInjector />
      <Link to="/kb" className="inline-flex items-center gap-1 text-sm text-slate-500 dark:text-slate-400 hover:text-slate-800 dark:hover:text-slate-100 dark:text-slate-400 dark:hover:text-slate-100">
        <ArrowLeft className="h-3.5 w-3.5" /> Knowledge Base
      </Link>
      <div>
        <h1 className="flex items-center gap-2 text-2xl font-bold text-slate-900 dark:text-slate-100 dark:text-slate-50">
          <AlertTriangle className="h-6 w-6" /> Callout audit
        </h1>
        <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
          Every Danger and Warning box in the KB in one place, so risky steps can be reviewed
          together — including the boxes carried over from the old KB.
        </p>
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <div className="relative max-w-md flex-1">
          <Search className="pointer-events-none absolute left-2.5 top-2.5 h-4 w-4 text-slate-400 dark:text-slate-500" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} className="pl-8"
            placeholder="Search callout text or article title" data-testid="kb-audit-search" />
        </div>
        <div className="text-sm text-slate-500 dark:text-slate-400" data-testid="kb-audit-summary">
          {data ? `${data.total} callout${data.total === 1 ? "" : "s"} across ${data.articles} article${data.articles === 1 ? "" : "s"}` : "…"}
        </div>
        <Button variant="ghost" size="sm" onClick={() => setVariants(DEFAULTS)}
          data-testid="kb-audit-reset">Danger + Warning only</Button>
        <Button variant="ghost" size="sm"
          onClick={() => setVariants((data?.counts || []).map((c) => c.variant))}
          data-testid="kb-audit-all">All types</Button>
        <Button variant="outline" size="sm" onClick={() => download("csv")} data-testid="kb-audit-csv">
          <Table className="mr-1 h-3.5 w-3.5" /> CSV
        </Button>
        <Button variant="outline" size="sm" onClick={() => download("pdf")} data-testid="kb-audit-pdf">
          <FileDown className="mr-1 h-3.5 w-3.5" /> PDF
        </Button>
        {loading && <Loader2 className="h-4 w-4 animate-spin text-slate-400 dark:text-slate-500" />}
      </div>

      <div className="flex flex-wrap items-center gap-1.5" data-testid="kb-audit-flags">
        <button type="button" onClick={() => setFlag(flag === "risk" ? "" : "risk")}
          data-testid="kb-audit-flag-risk"
          className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs font-semibold ${flag === "risk"
            ? "border-rose-400 bg-rose-50 text-rose-700 dark:border-rose-800 dark:bg-rose-950/40 dark:text-rose-300"
            : "border-slate-200 dark:border-slate-800 text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800 dark:border-slate-700 dark:text-slate-300 dark:hover:bg-slate-800"}`}>
          <ShieldAlert className="h-3.5 w-3.5" /> Looks risky ({data?.risk_suggestions ?? 0})
        </button>
        {data?.can_edit && !!data?.risk_suggestions && (
          <Link to="/kb/risk-review" data-testid="kb-audit-review-link">
            <Button size="sm" variant="outline" className="h-7 px-2 text-xs">
              Review them one by one
            </Button>
          </Link>
        )}
        {data?.can_edit && (
          <Link to="/kb/danger-review" data-testid="kb-audit-danger-link">
            <Button size="sm" variant="outline" className="h-7 px-2 text-xs">
              Danger downgrade pass
            </Button>
          </Link>
        )}
        <button type="button" onClick={() => setFlag(flag === "empty" ? "" : "empty")}
          data-testid="kb-audit-flag-empty"
          className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs font-semibold ${flag === "empty"
            ? "border-amber-400 bg-amber-50 text-amber-800 dark:border-amber-800 dark:bg-amber-950/40 dark:text-amber-300"
            : "border-slate-200 dark:border-slate-800 text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800 dark:border-slate-700 dark:text-slate-300 dark:hover:bg-slate-800"}`}>
          Needs finishing ({data?.unfinished ?? 0})
        </button>
        {flag === "risk" && (
          <span className="text-xs text-slate-500 dark:text-slate-400">
            Warning boxes whose wording suggests a real danger — promote the ones you agree with.
          </span>
        )}
        {flag === "empty" && (
          <span className="text-xs text-slate-500 dark:text-slate-400">
            Boxes with no text, under 15 characters, or just TBD/TODO.
          </span>
        )}
      </div>

      {!!Object.keys(undos).length && (
        <Card className="flex flex-wrap items-center gap-2 border-primary/40 bg-primary/5 p-3"
          data-testid="kb-audit-changes">
          <span className="text-xs font-semibold text-slate-700 dark:text-slate-200">
            Changed in this session:
          </span>
          {Object.entries(undos).map(([rowId, u]) => (
            <span key={rowId} className="inline-flex items-center gap-1 rounded-full border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 px-2 py-0.5 text-[11px] dark:border-slate-700 dark:bg-slate-900">
              {u.article} · {u.before} → danger{u.modernised ? " (modernised)" : ""}
              <Button size="sm" variant="ghost" disabled={busy === rowId}
                onClick={() => undo(rowId)} data-testid={`kb-audit-undo-bar-${rowId}`}
                className="h-5 px-1.5 text-[11px] font-semibold text-primary">
                <Undo2 className="mr-1 h-3 w-3" /> Undo
              </Button>
            </span>
          ))}
        </Card>
      )}

      <div className="flex flex-wrap items-center gap-1.5" data-testid="kb-audit-types">
        {(data?.counts || []).map((c) => (
          <button key={c.variant} type="button" onClick={() => toggle(c.variant)}
            data-testid={`kb-audit-type-${c.variant}`}
            className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs font-medium ${variants.includes(c.variant)
              ? "border-primary bg-primary/10 text-primary"
              : "border-slate-200 dark:border-slate-800 text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800 dark:border-slate-700 dark:text-slate-300 dark:hover:bg-slate-800"}`}>
            <span className={`h-2 w-2 rounded-full kb-swatch-${c.variant}`} />
            {c.label} ({c.count})
          </button>
        ))}
      </div>

      <div className="space-y-2">
        {groups.slice(0, limit).map((g) => (
          <Card key={g.slug} className="p-4" data-testid={`kb-audit-article-${g.slug}`}>
            <div className="flex flex-wrap items-center gap-2">
              <Link to={`/kb/a/${g.slug}`} className="font-semibold text-slate-800 dark:text-slate-100 hover:underline dark:text-slate-100"
                data-testid={`kb-audit-open-${g.slug}`}>
                {g.article}
              </Link>
              <span className="text-xs text-slate-500 dark:text-slate-400">{g.category}</span>
              {g.rows[0].draft && <span className="rounded bg-amber-100 px-1.5 py-0.5 text-[10px] font-semibold uppercase text-amber-700 dark:bg-amber-950/50 dark:text-amber-300">Draft</span>}
              <span className="ml-auto flex items-center gap-2">
                <span className="text-xs text-slate-400 dark:text-slate-500">{g.rows.length} box{g.rows.length === 1 ? "" : "es"}</span>
                <Link to={`/kb/a/${g.slug}`}>
                  <Button size="sm" variant="outline" className="h-7 px-2 text-xs">
                    <ExternalLink className="mr-1 h-3.5 w-3.5" /> Open
                  </Button>
                </Link>
                {data?.can_edit && (
                  <Link to={`/kb/a/${g.slug}/edit`} data-testid={`kb-audit-edit-${g.slug}`}>
                    <Button size="sm" variant="ghost" className="h-7 px-2 text-xs">
                      <Pencil className="mr-1 h-3.5 w-3.5" /> Edit
                    </Button>
                  </Link>
                )}
              </span>
            </div>
            <div className="mt-2 space-y-2">
              {g.rows.map((c) => (
                <div key={c.id} className="rounded-lg border border-slate-200 dark:border-slate-800 p-2.5 dark:border-slate-700"
                  data-testid={`kb-audit-row-${c.id}`}>
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="inline-flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-wide text-slate-700 dark:text-slate-200">
                      <span className={`h-2.5 w-2.5 rounded-full kb-swatch-${c.variant}`} />
                      {c.label}
                    </span>
                    {c.title && <span className="text-sm font-semibold text-slate-800 dark:text-slate-100">{c.title}</span>}
                    {c.suggest_danger && (
                      <span className="inline-flex items-center gap-1 rounded bg-rose-50 px-1.5 py-0.5 text-[10px] font-semibold text-rose-700 dark:bg-rose-950/40 dark:text-rose-300"
                        data-testid={`kb-audit-risk-${c.id}`}>
                        <ShieldAlert className="h-3 w-3" /> {c.risk.join(", ")}
                      </span>
                    )}
                    {c.unfinished && (
                      <span className="rounded bg-amber-50 px-1.5 py-0.5 text-[10px] font-semibold text-amber-800 dark:bg-amber-950/40 dark:text-amber-300"
                        data-testid={`kb-audit-unfinished-${c.id}`}>
                        needs finishing
                      </span>
                    )}
                    <span className="ml-auto flex items-center gap-1">
                      {c.imported && <span className="text-[10px] uppercase tracking-wide text-slate-400 dark:text-slate-500">imported</span>}
                      {undos[c.id] && (
                        <Button size="sm" variant="ghost" disabled={busy === c.id}
                          onClick={() => undo(c.id)} data-testid={`kb-audit-undo-${c.id}`}
                          className="h-6 px-2 text-[11px] font-semibold text-primary">
                          <Undo2 className="mr-1 h-3 w-3" /> Undo ({undos[c.id].before})
                        </Button>
                      )}
                      {data?.can_edit && c.variant !== "danger" && (
                        <Button size="sm" variant="ghost" disabled={busy === c.id}
                          onClick={() => promote(c)} data-testid={`kb-audit-promote-${c.id}`}
                          className="h-6 px-2 text-[11px] text-rose-600 hover:bg-rose-50 hover:text-rose-700 dark:hover:bg-rose-950/40">
                          {busy === c.id ? <Loader2 className="h-3 w-3 animate-spin" /> : "Make it Danger"}
                        </Button>
                      )}
                      {data?.can_edit && c.imported && (
                        <Button size="sm" variant="ghost" disabled={busy === c.id}
                          onClick={() => promote(c, true)} data-testid={`kb-audit-modernise-${c.id}`}
                          title="Rebuild this imported box as a house callout so it follows your KB styling"
                          className="h-6 px-2 text-[11px] text-slate-500 dark:text-slate-400 hover:text-primary">
                          <Sparkles className="mr-1 h-3 w-3" /> Modernise
                        </Button>
                      )}
                    </span>
                  </div>
                  <p className="mt-1.5 text-sm text-slate-600 dark:text-slate-300">
                    {c.text || <em className="text-slate-400 dark:text-slate-500">Empty box</em>}
                  </p>
                </div>
              ))}
            </div>
          </Card>
        ))}
        {groups.length > limit && (
          <Button variant="outline" className="w-full" onClick={() => setLimit((l) => l + 40)}
            data-testid="kb-audit-more">
            Show more ({groups.length - limit} more article{groups.length - limit === 1 ? "" : "s"})
          </Button>
        )}
        {!loading && !groups.length && (
          <Card className="p-6 text-center text-sm text-slate-500 dark:text-slate-400" data-testid="kb-audit-empty">
            No callouts of those types.
          </Card>
        )}
      </div>
    </div>
  );
}
